import type { Operation, OperationFile } from '@gmn-portal/backend';
import {
  Activity,
  Building2,
  CheckCircle2,
  Circle,
  FileText,
  MinusCircle,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  Wrench,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  type OperationFileQueryParams,
  payloadClient,
  useOperation,
  useOperationFiles,
  useUpdateOperation,
  useUpdateOperationFile,
  useUploadOperationFile,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { EntityPermissionsTab } from '@/features/shared/permissions';
import type { DetailPageHeaderProps } from '@/shared/components/layout';
import {
  DetailCard,
  DetailPageLayout,
  EntityReferenceValue,
  TabNav,
} from '@/shared/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Label,
} from '@/shared/components/ui';
import { getCustomerDetailHref, resolveFileUploadErrorMessage } from '@/shared/utils';
import {
  canReadService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';

import { OperationActivity } from '../components/operation-activity';

async function openProtectedFile(url: string, filename?: string) {
  const request = await payloadClient.createRequest(url.replace('/api', ''), { method: 'GET' });
  const response = await payloadClient.fetcher(request);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = objectUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.setAttribute('aria-label', filename || 'Open file');
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
}

const StatusBadge = ({ status }: { status: Operation['status'] }) => {
  const { t } = useIntl();
  if (status === 'closed') {
    return <Badge variant="outline-solid">{t('operations.status.closed')}</Badge>;
  }
  return (
    <Badge variant="outline-solid" className="text-green-600">
      {t('operations.status.open')}
    </Badge>
  );
};

const DecisionBadge = ({ decision }: { decision: Operation['followUpDecision'] }) => {
  const { t } = useIntl();
  switch (decision) {
    case 'yes':
      return (
        <Badge variant="outline-solid" className="text-green-600">
          {t('operations.followUpDecision.yes')}
        </Badge>
      );
    case 'no':
      return (
        <Badge variant="outline-solid" className="text-red-600">
          {t('operations.followUpDecision.no')}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline-solid" className="text-yellow-600">
          {t('operations.followUpDecision.pending')}
        </Badge>
      );
  }
};

const getUserFullNameLabel = (value: unknown): string => {
  if (!value) return '-';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') {
    return `${value}`;
  }
  const { firstName, lastName, id } = value as {
    firstName?: string;
    lastName?: string;
    id?: string | number | null;
  };
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  if (fullName) return fullName;
  if (id !== null && id !== undefined) return `${id}`;
  return '-';
};

const FileListItem = ({
  file,
  onDownload,
  downloadLabel,
  metaLabel,
  visibilityLabel,
  visibilityChecked,
  visibilityDisabled,
  onVisibilityChange,
}: {
  file: OperationFile;
  onDownload: (file: OperationFile) => void;
  downloadLabel: string;
  metaLabel?: string;
  visibilityLabel?: string;
  visibilityChecked?: boolean;
  visibilityDisabled?: boolean;
  onVisibilityChange?: (value: boolean | 'indeterminate') => void;
}) => (
  <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3">
    <div className="flex items-center gap-3 min-w-0">
      <div className="size-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
        <FileText className="size-4" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{file.filename || file.id}</div>
        <div className="text-xs text-muted-foreground">
          {metaLabel ??
            (file.createdAt ? DateTime.fromISO(file.createdAt).toFormat('dd.MM.yyyy HH:mm') : '-')}
        </div>
      </div>
    </div>
    <div className="flex items-center gap-3">
      {visibilityLabel && onVisibilityChange ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            id={`file-visible-${file.id}`}
            checked={!!visibilityChecked}
            onCheckedChange={onVisibilityChange}
            disabled={visibilityDisabled}
          />
          <Label htmlFor={`file-visible-${file.id}`}>{visibilityLabel}</Label>
        </div>
      ) : null}
      <Button variant="outline" onClick={() => onDownload(file)} disabled={!file.url}>
        {downloadLabel}
      </Button>
    </div>
  </div>
);

const EmptyFileState = ({ label, hint }: { label: string; hint?: string }) => (
  <div className="rounded-lg border border-dashed border-border bg-muted/30 p-5 flex items-center gap-3 text-sm text-muted-foreground">
    <FileText className="size-4" />
    <div>
      <div>{label}</div>
      {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  </div>
);

const TimelineStatusBadge = ({
  status,
}: {
  status: 'done' | 'current' | 'pending' | 'skipped';
}) => {
  const { t } = useIntl();

  if (status === 'done') {
    return (
      <Badge variant="outline-solid" className="text-green-600">
        {t('operations.timeline.status.done')}
      </Badge>
    );
  }

  if (status === 'current') {
    return (
      <Badge variant="outline-solid" className="text-yellow-600">
        {t('operations.timeline.status.current')}
      </Badge>
    );
  }

  if (status === 'skipped') {
    return (
      <Badge variant="outline-solid" className="text-muted-foreground">
        {t('operations.timeline.status.skipped')}
      </Badge>
    );
  }

  return <Badge variant="outline-solid">{t('operations.timeline.status.pending')}</Badge>;
};

const getTimelineIcon = (status: 'done' | 'current' | 'pending' | 'skipped') => {
  if (status === 'done') {
    return <CheckCircle2 className="size-4 text-green-600" />;
  }
  if (status === 'current') {
    return <Circle className="size-4 text-yellow-600" />;
  }
  if (status === 'skipped') {
    return <MinusCircle className="size-4 text-muted-foreground" />;
  }
  return <Circle className="size-4 text-muted-foreground" />;
};

export function OperationDetails() {
  const { t, locale } = useIntl();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const followupInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const toastMessage = (key: string) => String(t(key as never));

  const group = resolveUserGroup(user);
  const groupKey = group?.key ?? null;
  const isGmnAdmin = groupKey === 'gmn-admin';
  const isCustomerAdmin = groupKey === 'customer-admin';
  const domain = resolveUserGroupDomain(user);
  const basePath = domain === 'customer' ? '/c/my-operations' : '/gmn/operations';
  const canReadCustomers = canReadService(group, 'customers');
  const effectiveUserCustomerId =
    typeof user?.customer === 'object' ? user.customer?.id : (user?.customer as string | undefined);

  const { data: operation } = useOperation(id!, { disabled: !id, depth: 2 });
  const updateOperation = useUpdateOperation();
  const updateOperationFile = useUpdateOperationFile();
  const uploadOperationFile = useUploadOperationFile();

  const operationId = id || '';

  const followupFilesQuery: OperationFileQueryParams = {
    disabled: !operationId,
    where: {
      and: [{ operation: { equals: operationId } }, { kind: { equals: 'followup-protocol' } }],
    },
    sort: '-createdAt',
    limit: 50,
  };
  const { data: followupFilesData } = useOperationFiles(followupFilesQuery);

  const attachmentFilesQuery: OperationFileQueryParams = {
    disabled: !operationId,
    where: {
      and: [{ operation: { equals: operationId } }, { kind: { equals: 'attachment' } }],
    },
    sort: '-createdAt',
    limit: 50,
  };
  const { data: attachmentFilesData } = useOperationFiles(attachmentFilesQuery);

  const currentTab = location.pathname.split('/').pop();
  const showTabs = isGmnAdmin || isCustomerAdmin;

  const tabs = useMemo(
    () => [
      { value: 'details', label: t('common.general'), icon: FileText },
      ...(showTabs
        ? [{ value: 'permissions', label: t('permissions.tab'), icon: ShieldCheck }]
        : []),
      { value: 'activity', label: t('common.activity'), icon: Activity },
    ],
    [showTabs, t]
  );

  const [confirmDecision, setConfirmDecision] = useState<'yes' | 'no' | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const followupFiles = (followupFilesData?.docs || []) as OperationFile[];
  const attachmentFiles = (attachmentFilesData?.docs || []) as OperationFile[];
  const followupProtocolFile = followupFiles[0];

  if (!operation) {
    return null;
  }

  const customerName =
    typeof operation.customer === 'object'
      ? (operation.customer as any).name
      : (operation.customer as string);
  const operationCustomerId =
    typeof operation.customer === 'object'
      ? (operation.customer as any).id
      : (operation.customer as string);
  const operationCustomerType =
    typeof operation.customer === 'object' ? (operation.customer as any).customerType : undefined;
  const operationCustomerHref = getCustomerDetailHref({
    customerId: operationCustomerId,
    customerType: operationCustomerType,
    domain,
    canReadCustomers,
    effectiveUserCustomerId,
  });
  const responsibleLabel = getUserFullNameLabel(operation.responsibleGmnUser);
  const assignedLabel = getUserFullNameLabel(operation.assignedCustomerUser);

  const reportFile = operation.reportFile as OperationFile | string | null;
  const reportFileDoc = typeof reportFile === 'object' ? (reportFile as OperationFile) : null;
  const isAssignedCustomerUser =
    !!user?.id &&
    (typeof operation.assignedCustomerUser === 'string'
      ? operation.assignedCustomerUser === user.id
      : operation.assignedCustomerUser?.id === user.id);

  const canDecide =
    (isCustomerAdmin || isAssignedCustomerUser) &&
    operation.status === 'open' &&
    operation.followUpDecision === 'pending';
  const canClose = isGmnAdmin && operation.status === 'open';

  const handleDecision = async (decision: 'yes' | 'no') => {
    try {
      await (updateOperation as any).mutateAsync({
        id: operation.id,
        data: { followUpDecision: decision },
      });
      toast.success(toastMessage('operations.toasts.decisionSaved'));
    } catch {
      toast.error(toastMessage('operations.toasts.decisionFailed'));
    } finally {
      setConfirmDecision(null);
    }
  };

  const handleClose = async () => {
    try {
      await (updateOperation as any).mutateAsync({
        id: operation.id,
        data: { status: 'closed' },
      });
      toast.success(toastMessage('operations.toasts.closed'));
    } catch {
      toast.error(toastMessage('operations.toasts.closeFailed'));
    } finally {
      setConfirmClose(false);
    }
  };

  const uploadFile = async (kind: 'followup-protocol' | 'attachment', file: File) => {
    try {
      await (uploadOperationFile as any).mutateAsync({
        file,
        data: {
          kind,
          operation: operation.id,
          customerVisible: false,
        },
      });
      toast.success(toastMessage('operations.toasts.fileUploaded'));
    } catch (error) {
      toast.error(resolveFileUploadErrorMessage(error, t, 'operations.toasts.fileUploadFailed'));
    }
  };

  const handleVisibilityChange = async (
    file: OperationFile,
    nextValue: boolean | 'indeterminate'
  ) => {
    if (nextValue === 'indeterminate') return;
    try {
      await (updateOperationFile as any).mutateAsync({
        id: file.id,
        data: {
          customerVisible: nextValue,
        },
      });
      toast.success(toastMessage('operations.toasts.fileVisibilityUpdated'));
    } catch {
      toast.error(toastMessage('operations.toasts.fileVisibilityUpdateFailed'));
    }
  };

  const isDetailsTab = !showTabs || currentTab === 'details' || currentTab === id;

  const headerMetaItems: DetailPageHeaderProps['metaItems'] = [
    ...(customerName
      ? [{ icon: Building2, label: customerName, href: operationCustomerHref ?? undefined }]
      : []),
    { icon: FileText, label: t('operations.meta.report') },
  ];

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref: basePath,
        backLabel: t('operations.backToOverview'),
        title: operation.title || t('operations.operationDetails'),
        icon: Wrench,
        metaItems: headerMetaItems,
      }}
    >
      {showTabs && (
        <TabNav tabs={tabs} basePath={`${basePath}/${operation.id}`} variant="underline" />
      )}

      {isDetailsTab ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <DetailCard
            icon={FileText}
            title={t('operations.summary.title')}
            className="lg:col-span-2"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1 md:col-span-2">
                <div className="text-sm text-muted-foreground">{t('operations.fields.title')}</div>
                <div className="text-sm font-medium">{operation.title || '-'}</div>
              </div>
              <div className="space-y-1 md:col-span-2">
                <div className="text-sm text-muted-foreground">
                  {t('operations.fields.description')}
                </div>
                <div className="text-sm">{operation.description || '-'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">{t('operations.fields.status')}</div>
                <StatusBadge status={operation.status} />
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  {t('operations.fields.followUpDecision')}
                </div>
                <DecisionBadge decision={operation.followUpDecision} />
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">{t('customers.customer')}</div>
                <div className="text-sm">
                  <EntityReferenceValue label={customerName} href={operationCustomerHref} />
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  {t('operations.fields.assignedCustomerUser')}
                </div>
                <div className="text-sm">{assignedLabel || '-'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">
                  {t('operations.fields.responsibleGmnUser')}
                </div>
                <div className="text-sm">{responsibleLabel || '-'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">{t('common.createdAt')}</div>
                <div className="text-sm">
                  {operation.createdAt
                    ? DateTime.fromISO(operation.createdAt).toFormat('dd.MM.yyyy HH:mm', {
                        locale,
                      })
                    : '-'}
                </div>
              </div>
            </div>
          </DetailCard>

          <DetailCard
            icon={FileText}
            title={t('operations.timeline.title')}
            className="lg:col-span-2"
          >
            <div className="space-y-6">
              {(
                [
                  {
                    id: 'report',
                    title: t('operations.timeline.reportTitle'),
                    status: reportFileDoc ? 'done' : 'pending',
                    content: reportFileDoc ? (
                      <FileListItem
                        file={reportFileDoc}
                        downloadLabel={t('operations.files.open')}
                        metaLabel={t('operations.files.singleFileHint')}
                        onDownload={(file) =>
                          file.url && openProtectedFile(file.url, file.filename || undefined)
                        }
                      />
                    ) : (
                      <EmptyFileState
                        label={t('operations.files.empty')}
                        hint={t('operations.files.singleFileHint')}
                      />
                    ),
                  },
                  {
                    id: 'followup',
                    title: t('operations.timeline.followupDecisionTitle'),
                    status:
                      operation.followUpDecision === 'pending'
                        ? 'current'
                        : operation.followUpDecision === 'yes'
                          ? 'done'
                          : 'skipped',
                    content: (
                      <>
                        <p className="text-sm text-muted-foreground mb-4">
                          {t(
                            isGmnAdmin
                              ? 'operations.followUp.descriptionGmn'
                              : 'operations.followUp.descriptionCustomer'
                          )}
                        </p>

                        {canDecide ? (
                          <div className="flex flex-wrap gap-3">
                            <Button onClick={() => setConfirmDecision('yes')} className="gap-2">
                              <ThumbsUp className="size-4" />
                              {t('operations.followUp.yes')}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setConfirmDecision('no')}
                              className="gap-2"
                            >
                              <ThumbsDown className="size-4" />
                              {t('operations.followUp.no')}
                            </Button>
                          </div>
                        ) : (
                          <div className="text-sm">
                            {t('operations.followUp.alreadyDecided')}{' '}
                            <DecisionBadge decision={operation.followUpDecision} />
                          </div>
                        )}
                      </>
                    ),
                  },
                  {
                    id: 'protocol',
                    title: t('operations.timeline.followupProtocolTitle'),
                    status: followupProtocolFile ? 'done' : isGmnAdmin ? 'current' : 'pending',
                    content: (
                      <div className="space-y-4">
                        {followupProtocolFile ? (
                          <FileListItem
                            file={followupProtocolFile}
                            downloadLabel={t('operations.files.open')}
                            metaLabel={t('operations.files.singleFileHint')}
                            visibilityLabel={
                              isGmnAdmin ? t('operations.files.visibleToCustomer') : undefined
                            }
                            visibilityChecked={!!followupProtocolFile.customerVisible}
                            visibilityDisabled={updateOperationFile.isPending}
                            onVisibilityChange={
                              isGmnAdmin
                                ? (value) => handleVisibilityChange(followupProtocolFile, value)
                                : undefined
                            }
                            onDownload={(file) =>
                              file.url && openProtectedFile(file.url, file.filename || undefined)
                            }
                          />
                        ) : (
                          <EmptyFileState
                            label={
                              isGmnAdmin
                                ? t('operations.timeline.followupProtocolEmpty')
                                : t('operations.timeline.followupProtocolNotShared')
                            }
                            hint={t('operations.files.singleFileHint')}
                          />
                        )}

                        {isGmnAdmin && (
                          <div className="space-y-3">
                            <div className="text-sm text-muted-foreground">
                              {t('operations.files.singleUploadHint')}
                            </div>
                            <div className="flex items-center gap-3">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => followupInputRef.current?.click()}
                                disabled={uploadOperationFile.isPending}
                              >
                                {followupProtocolFile
                                  ? t('operations.files.replace')
                                  : t('operations.files.upload')}
                              </Button>
                              <input
                                ref={followupInputRef}
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  if (file) void uploadFile('followup-protocol', file);
                                  event.currentTarget.value = '';
                                }}
                                disabled={uploadOperationFile.isPending}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    id: 'close',
                    title: t('operations.timeline.closeTitle'),
                    status: operation.status === 'closed' ? 'done' : 'pending',
                    content: canClose ? (
                      <div className="flex items-center justify-between gap-4">
                        <div className="text-sm text-muted-foreground">
                          {t('operations.actions.closeHint')}
                        </div>
                        <Button variant="destructive" onClick={() => setConfirmClose(true)}>
                          {t('operations.actions.close')}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {operation.status === 'closed'
                          ? t('operations.timeline.closeDone')
                          : t('operations.timeline.closeWaiting')}
                      </div>
                    ),
                  },
                ] as const
              ).map((step, index, all) => (
                <div key={step.id} className="relative flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="size-8 rounded-full bg-muted flex items-center justify-center">
                      {getTimelineIcon(step.status)}
                    </div>
                    {index < all.length - 1 && (
                      <div className="w-px flex-1 bg-border mt-2" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2 pb-4">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold">{step.title}</div>
                      <TimelineStatusBadge status={step.status} />
                    </div>
                    <div>{step.content}</div>
                  </div>
                </div>
              ))}
            </div>
          </DetailCard>

          <DetailCard
            icon={FileText}
            title={t('operations.files.attachments')}
            className="lg:col-span-2"
          >
            <div className="space-y-4">
              {attachmentFiles.length === 0 ? (
                <EmptyFileState label={t('operations.files.empty')} />
              ) : (
                <div className="space-y-3">
                  {attachmentFiles.map((f) => (
                    <div key={f.id} className="space-y-2">
                      <FileListItem
                        file={f}
                        downloadLabel={t('operations.files.open')}
                        visibilityLabel={
                          isGmnAdmin ? t('operations.files.visibleToCustomer') : undefined
                        }
                        visibilityChecked={!!f.customerVisible}
                        visibilityDisabled={updateOperationFile.isPending}
                        onVisibilityChange={
                          isGmnAdmin ? (value) => handleVisibilityChange(f, value) : undefined
                        }
                        onDownload={(file) =>
                          file.url && openProtectedFile(file.url, file.filename || undefined)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              {isGmnAdmin && (
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    {t('operations.files.multiUploadHint')}
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => attachmentInputRef.current?.click()}
                      disabled={uploadOperationFile.isPending}
                    >
                      {t('operations.files.upload')}
                    </Button>
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void uploadFile('attachment', file);
                        event.currentTarget.value = '';
                      }}
                      disabled={uploadOperationFile.isPending}
                    />
                  </div>
                </div>
              )}
            </div>
          </DetailCard>
        </div>
      ) : currentTab === 'permissions' ? (
        <EntityPermissionsTab
          entityId={operation.id}
          entityType="operations"
          customerContexts={
            operationCustomerId
              ? [{ customerId: operationCustomerId, source: 'assigned-customer' }]
              : []
          }
        />
      ) : (
        <OperationActivity operationId={operation.id} />
      )}

      <AlertDialog
        open={confirmDecision !== null}
        onOpenChange={(open) => !open && setConfirmDecision(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('operations.followUp.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('operations.followUp.confirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDecision && void handleDecision(confirmDecision)}
              disabled={updateOperation.isPending}
            >
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClose} onOpenChange={(open) => !open && setConfirmClose(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('operations.actions.closeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('operations.actions.closeConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleClose()}
              disabled={updateOperation.isPending}
            >
              {t('operations.actions.close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DetailPageLayout>
  );
}
